import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Package, Check, X, Truck, Send } from 'lucide-react';

export default function PurchaseOrderTab() {
  const { user } = useAuth();
  // Manager-level roles see + can approve everything. Staff (bartender,
  // chef, shisha_expert) can DRAFT only — backend enforces the category
  // scope; we mirror it in the UI so the inventory dropdown / Send /
  // Receive controls collapse to a clean staff view.
  const role = user?.role;
  const isManagerLike = ['manager', 'director', 'admin', 'owner'].includes(role);
  const isPoStaff = ['bartender', 'chef', 'shisha_expert'].includes(role);
  const ROLE_TO_CATEGORY = useMemo(() => ({
    bartender: 'drinks_ingredient',
    chef: 'food_ingredient',
    shisha_expert: 'shisha_supply',
  }), []);
  const ROLE_TO_LABEL = {
    bartender: 'Bar', chef: 'Kitchen', shisha_expert: 'Shisha',
  };
  const myCategory = ROLE_TO_CATEGORY[role] || null;
  const myCategoryLabel = ROLE_TO_LABEL[role] || null;
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: '', supplier_name: '', items: [], notes: '', expected_delivery: '' });
  const [newItem, setNewItem] = useState({ inventory_item_id: '', item_name: '', quantity: '', unit_cost: '' });

  const fetchData = useCallback(async () => {
    try {
      const [poRes, supRes, invRes] = await Promise.all([api.get('/purchase-orders'), api.get('/suppliers'), api.get('/inventory')]);
      setOrders(poRes.data);
      setSuppliers(supRes.data);
      setInventory(invRes.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addItem = () => {
    if (!newItem.inventory_item_id || !newItem.quantity) { toast.error('Select item and quantity'); return; }
    setForm(p => ({ ...p, items: [...p.items, { ...newItem, quantity: parseFloat(newItem.quantity), unit_cost: parseFloat(newItem.unit_cost) || 0 }] }));
    setNewItem({ inventory_item_id: '', item_name: '', quantity: '', unit_cost: '' });
  };

  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const selectSupplier = (id) => {
    const sup = suppliers.find(s => s.id === id);
    setForm(p => ({ ...p, supplier_id: id, supplier_name: sup?.name || '' }));
  };

  const selectInvItem = (id) => {
    const item = inventory.find(i => i.id === id);
    setNewItem(p => ({ ...p, inventory_item_id: id, item_name: item?.name || '', unit_cost: item?.cost_per_unit ? String(item.cost_per_unit) : p.unit_cost }));
  };

  const supplierItems = (() => {
    let pool = inventory;
    // Staff can only see + add their own-category items. Managers see all.
    if (myCategory) pool = pool.filter(i => i.category === myCategory);
    return form.supplier_id ? pool.filter(i => i.supplier_id === form.supplier_id) : pool;
  })();

  const bulkAddSupplierItems = () => {
    if (!form.supplier_id) { toast.error('Pick a supplier first'); return; }
    let candidates = inventory.filter(i => i.supplier_id === form.supplier_id);
    if (myCategory) candidates = candidates.filter(c => c.category === myCategory);
    if (candidates.length === 0) {
      toast.error(myCategory
        ? `No ${myCategoryLabel} items linked to this supplier`
        : 'No items linked to this supplier yet');
      return;
    }
    setForm(p => ({
      ...p,
      items: [
        ...p.items,
        ...candidates
          .filter(c => !p.items.some(existing => existing.inventory_item_id === c.id))
          .map(c => ({
            inventory_item_id: c.id,
            item_name: c.name,
            quantity: Math.max(c.min_stock_level - c.current_stock, 1),
            unit_cost: c.cost_per_unit || 0,
          })),
      ],
    }));
    toast.success(`Added ${candidates.length} items from ${form.supplier_name}`);
  };

  const createPO = async () => {
    if (!form.supplier_id || form.items.length === 0) { toast.error('Select supplier and add items'); return; }
    try {
      await api.post('/purchase-orders', form);
      toast.success('Purchase order created');
      setFormOpen(false);
      setForm({ supplier_id: '', supplier_name: '', items: [], notes: '', expected_delivery: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const receivePO = async (id) => {
    try { await api.put(`/purchase-orders/${id}/receive`); toast.success('PO received - stock updated'); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const cancelPO = async (id) => {
    try { await api.put(`/purchase-orders/${id}/cancel`); toast.success('PO cancelled'); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const sendPO = async (id) => {
    try {
      await api.put(`/purchase-orders/${id}/send`);
      toast.success('PO emailed to supplier (CC: admin & manager)');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send PO');
    }
  };

  const autoDraftLowStock = async () => {
    try {
      const { data } = await api.post('/purchase-orders/auto-draft');
      if (data.drafted > 0) {
        toast.success(`Drafted ${data.drafted} PO${data.drafted === 1 ? '' : 's'} from low-stock alerts`);
      } else {
        toast.info('No new low-stock POs to draft right now');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-draft failed');
    }
  };

  const statusColors = { pending: 'bg-amber-500/20 text-amber-400', sent: 'bg-blue-accent/20 text-blue-accent', received: 'bg-emerald-500/20 text-emerald-400', cancelled: 'bg-red-500/20 text-red-400' };
  const poTotal = form.items.reduce((s, i) => s + i.quantity * i.unit_cost, 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider">
          {orders.length} purchase orders
          {myCategoryLabel && (
            <span className="ml-2 text-[10px] text-gold tracking-[0.18em] font-mono">· {myCategoryLabel.toUpperCase()} ONLY</span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {isManagerLike && (
            <Button size="sm" onClick={autoDraftLowStock} className="bg-blue-accent/15 text-blue-accent border border-blue-accent/30 hover:bg-blue-accent/25 rounded-sm font-sans text-xs" data-testid="auto-draft-po-button">
              <Truck className="w-3 h-3 mr-1" /> Auto-Draft Low Stock
            </Button>
          )}
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="create-po-button"><Plus className="w-3 h-3 mr-1" /> New PO</Button>
            </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-white">Create Purchase Order</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2 max-h-[65vh] overflow-y-auto">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Supplier</Label>
                <Select value={form.supplier_id} onValueChange={selectSupplier}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Items</Label>
                  {form.supplier_id && (
                    <Button size="sm" type="button" onClick={bulkAddSupplierItems} className="h-6 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-[11px]" data-testid="po-bulk-add-button">
                      + Add all linked items
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Select value={newItem.inventory_item_id} onValueChange={selectInvItem}>
                    <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans flex-1" data-testid="po-item-select"><SelectValue placeholder={form.supplier_id ? 'Pick from this supplier' : 'Item'} /></SelectTrigger>
                    <SelectContent className="bg-bg-secondary border border-white/10">
                      {supplierItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} className="w-20 bg-transparent border border-white/10 text-white font-sans" data-testid="po-item-qty" />
                  <Input type="number" placeholder="$/unit" step="0.01" value={newItem.unit_cost} onChange={e => setNewItem(p => ({ ...p, unit_cost: e.target.value }))} className="w-24 bg-transparent border border-white/10 text-white font-sans" data-testid="po-item-cost" />
                  <Button size="sm" onClick={addItem} className="bg-white/5 text-white rounded-sm"><Plus className="w-3 h-3" /></Button>
                </div>
                {form.items.map((item, idx) => (
                  <div key={`${item.item_id}-${idx}`} className="flex items-center justify-between p-2 bg-white/[0.03] border border-white/5 rounded-sm text-sm">
                    <span className="text-white font-sans">{item.item_name} x{item.quantity}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gold font-mono">${(item.quantity * item.unit_cost).toFixed(2)}</span>
                      <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                {form.items.length > 0 && <div className="text-right text-gold font-sans font-semibold">Total: ${poTotal.toFixed(2)}</div>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Expected Delivery</Label>
                  <Input type="date" value={form.expected_delivery} onChange={e => setForm(p => ({ ...p, expected_delivery: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="po-delivery-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Notes</Label>
                  <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="po-notes" />
                </div>
              </div>
              <Button onClick={createPO} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-po-button">Create Purchase Order</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="space-y-2">
        {orders.map(po => (
          <div key={po.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`po-${po.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Truck className="w-4 h-4 text-gold" />
                  <span className="text-white font-sans font-medium">{po.supplier_name}</span>
                  <Badge className={`${statusColors[po.status]} border-0 font-sans text-xs`}>{po.status}</Badge>
                  {po.auto_drafted && <Badge className="bg-blue-accent/15 text-blue-accent border-0 font-sans text-[10px] uppercase tracking-wider">Auto-drafted</Badge>}
                </div>
                <p className="text-zinc-500 text-xs font-sans mt-1">#{po.id.slice(0, 8)} - {new Date(po.created_at).toLocaleDateString()}</p>
              </div>
              <span className="text-gold font-mono font-semibold">${po.total?.toFixed(2)}</span>
            </div>
            <div className="space-y-1 mb-2">
              {po.items?.map((item, idx) => (
                <div key={`${po.id}-${item.item_id || idx}`} className="flex justify-between text-sm">
                  <span className="text-zinc-300 font-sans">{item.item_name} x{item.quantity}</span>
                  <span className="text-zinc-500 font-mono">${(item.quantity * item.unit_cost).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {(po.status === 'pending' || po.status === 'sent') && (
              <div className="flex gap-2 pt-2 border-t border-white/5 flex-wrap">
                {/* Send/Receive locked to manager+director per owner policy.
                    Staff (bartender/chef/shisha_expert) only get the
                    Cancel button on their own pending drafts. */}
                {po.status === 'pending' && isManagerLike && (
                  <Button size="sm" onClick={() => sendPO(po.id)} className="h-7 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-xs" data-testid={`send-po-${po.id}`}><Send className="w-3 h-3 mr-1" /> Send to Supplier</Button>
                )}
                {isManagerLike && (
                  <Button size="sm" onClick={() => receivePO(po.id)} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid={`receive-po-${po.id}`}><Check className="w-3 h-3 mr-1" /> Receive</Button>
                )}
                {(isManagerLike || (isPoStaff && po.status === 'pending' && po.created_by === user?.id)) && (
                  <Button size="sm" onClick={() => cancelPO(po.id)} className="h-7 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-sm font-sans text-xs" data-testid={`cancel-po-${po.id}`}><X className="w-3 h-3 mr-1" /> Cancel</Button>
                )}
                {/* Staff awaiting approval — surface a clear "Pending Manager Approval" pill */}
                {!isManagerLike && po.status === 'pending' && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm font-sans">
                    <Send className="w-3 h-3" /> Awaiting Manager Approval
                  </span>
                )}
              </div>
            )}
            {po.sent_at && (
              <p className="text-[11px] text-zinc-500 font-sans mt-2">Emailed to {po.sent_to} on {new Date(po.sent_at).toLocaleString()}</p>
            )}
          </div>
        ))}
        {orders.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-purchase-orders">No purchase orders yet</p>}
      </div>
    </div>
  );
}
